/**
 * "On Charge" — whether the cost of a work order gets recovered from someone
 * else (CA, dealer, landlord, insurer, builder) once AP has paid the invoice.
 *
 * The value lives in MC on WO.ShiftID, surfaced by the API as ShiftRef.ID. That
 * is not a typo: MC's stock "Shift" field was repurposed years ago, and it is
 * what United's own reports read — "All Work Orders x Requested Date - AP" and
 * the "Oncharge Work Order" reports all map WO.ShiftID with a label override of
 * "On Charge", and the WO form prints it as "Cost To Be On Charged".
 *
 * Do NOT use WO.Chargeable for this. It is a different, largely abandoned flag
 * (~80 ticked in all of 2026) that drives MC's charge markup, and it disagrees
 * with the on-charge answer: QLD-441764 is Chargeable=true / ShiftID='NO'.
 *
 * The field is free text, so the answer is derived rather than looked up.
 * Everything that is not blank, not 'NO', and not explicitly undecided is an
 * on-charge — that covers 'Insurance Recovery', 'Previous CA/DLR100',
 * 'Sub-Tenant Charge' and friends, which are recoveries that just do not happen
 * to start with the word "Yes". The raw text is always carried alongside the
 * verdict so AP can see WHO it goes to, which is the part that decides where the
 * on-charge request is sent.
 */

export const ON_CHARGE_YES = 'Yes';
export const ON_CHARGE_NO = 'No';
export const ON_CHARGE_TBC = 'To be confirmed';
export const ON_CHARGE_UNSET = 'Not set';

/**
 * @param {string} raw  WO.ShiftRef.ID as returned by the API
 * @returns {{answer: string, detail: string, raw: string}}
 *          answer  — Yes / No / To be confirmed / Not set
 *          detail  — who it is recovered from ('Dealer'), '' when there is none
 */
export function onCharge(raw) {
    const value = (raw || '').trim();
    if (!value) return { answer: ON_CHARGE_UNSET, detail: '', raw: '' };

    const lower = value.toLowerCase();

    if (lower === 'no') return { answer: ON_CHARGE_NO, detail: '', raw: value };

    if (lower.startsWith('tentative') || lower.startsWith('to be determined')) {
        return { answer: ON_CHARGE_TBC, detail: '', raw: value };
    }

    // 'Yes - Dealer' -> detail 'Dealer'. Anything else keeps its whole text as
    // the detail, since that text IS the counterparty ('Insurance Recovery').
    // MC truncates ShiftID at 25 chars, so values like 'Yes - Current CA (Partial'
    // arrive with the bracket unclosed — tidy that rather than show it raw.
    let detail = lower.startsWith('yes') ? value.replace(/^yes\s*[-–]?\s*/i, '') : value;
    detail = detail.trim().replace(/\($/, '').trim();
    if (/\([^)]*$/.test(detail)) detail += ')';

    return { answer: ON_CHARGE_YES, detail, raw: value };
}

/** One-line form for email and plain-text output: "Yes - Dealer" / "No". */
export function onChargeText(raw) {
    const { answer, detail } = onCharge(raw);
    return detail ? `${answer} - ${detail}` : answer;
}
